import {
  customerIsCompany,
  customerIsMixed,
  userIsIndividual,
} from '@/helpers/customerUtils';
import { bffRequest, createMittBffClient } from '@/app/bffClient';
import { Account, Customer } from '@/types/Customer';
import { LinkedUser, Organization, Property, State } from '@/types/State';
import { Dispatch, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  addCompanyAccountForUser,
  getCustomer,
  getCustomerFacilities,
  getCustomerPanelParticipation,
  getLinkedUserAccountsForCustomer,
  getParentOrganization,
  getUserAccountDetails,
  removeUserAccountsFromCustomer,
  updateCustomerAccountDetails,
  updateCustomerPanelParticipation,
} from 'bff-api/mitt-malarenergi/v2';
import { filter, find, first, get, includes, map } from 'lodash';
import { NON_CUSTOMER_CUSTOMER_ID } from '../../helpers/constants';

interface UserState {
  account: Account | null;
  customer: Customer | null;
  selectedCustomer: Customer | null;
  selectedCustomerAccountId: string | null;
  selectedProperty: Property | null;
  organization: Organization | null;
}

export const userSlice = createSlice({
  name: 'user',
  initialState: {
    account: null,
    customer: null,
    selectedCustomer: null,
    selectedCustomerAccountId: null,
    selectedProperty: null,
  } as UserState,
  reducers: {
    setAccount: (state, action) => {
      state.account = action.payload;
    },
    setCustomer: (state, action) => {
      state.customer = action.payload;
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    setSelectedProperty: (state, action) => {
      state.selectedProperty = action.payload;
    },
    setUserDetails: (state, action) => {
      if (state.customer) {
        state.customer.customerAccounts = state.customer.customerAccounts.map(
          (account) => ({
            ...account,
            ...action.payload,
            mobilePhone: action.payload.phoneNumber,
            email: action.payload.email,
          }),
        );
        state.customer.email = action.payload.email;
        state.customer.mobilePhone = action.payload.phoneNumber;
      }
      if (state.selectedCustomer) {
        state.selectedCustomer.customerAccounts =
          state.selectedCustomer.customerAccounts.map((account) => ({
            ...account,
            ...action.payload,
            mobilePhone: action.payload.phoneNumber,
            email: action.payload.email,
          }));
        state.selectedCustomer.email = action.payload.email;
        state.selectedCustomer.mobilePhone = action.payload.phoneNumber;
      }
    },
    setSelectedCustomerFacilities: (state, action) => {
      if (state.selectedCustomer) {
        state.selectedCustomer.facilities = map(
          state.selectedCustomer.facilities,
          (facility) => {
            const payloadFacility = find(action.payload, {
              facilityKey: facility.facilityKey,
            });
            return { ...facility, ...payloadFacility };
          },
        );
      }
    },
    setSelectedCustomerUseNewLandingPage: (state, action) => {
      if (state.selectedCustomer) {
        state.selectedCustomer.useNewLandingPage = action.payload;
      }
    },
    removeLinkedUser: (state, action) => {
      const { user, customerId } = action.payload;
      if (state.selectedCustomer) {
        state.selectedCustomer.linkedUsers = filter(
          state.selectedCustomer.linkedUsers,
          (u) => u.userId !== user.userId,
        );
      }
      if (customerId === get(state, 'customer.customerId') && state.customer) {
        state.customer.linkedUsers = filter(
          state.customer.linkedUsers,
          (u) => u.userId !== user.userId,
        );
      }
    },
    setSelectedCustomerAccountId: (state, action) => {
      state.selectedCustomerAccountId = action.payload;
    },
    setOrganizationStructure: (state, action) => {
      state.organization = action.payload;
    },
  },
});

export const {
  setAccount,
  setCustomer,
  setSelectedCustomer,
  setSelectedProperty,
  setUserDetails,
  removeLinkedUser,
  setSelectedCustomerAccountId,
  setSelectedCustomerFacilities,
  setOrganizationStructure,
  setSelectedCustomerUseNewLandingPage,
} = userSlice.actions;

export const fetchAccount = () => (dispatch: Dispatch) =>
  bffRequest((client) =>
    getUserAccountDetails({ client, throwOnError: true }),
  )
    .then((envelope) => envelope?.data?.[0])
    .then((account) => dispatch(setAccount(account)))
    .catch(console.error);

export const fetchOrganizationStructure =
  (customerId: string) => (dispatch: Dispatch) =>
    bffRequest((client) =>
      getParentOrganization({
        client,
        path: { customerId: Number(customerId) },
        throwOnError: true,
      }),
    )
      .then((envelope) => envelope?.data?.[0])
      .then((structure) => dispatch(setOrganizationStructure(structure)))
      .catch((error) => {
        error?.status === 404
          ? dispatch(setOrganizationStructure(null))
          : console.error(error);
      });

export const fetchAccountCustomerId =
  (customerId: number) => (dispatch: Dispatch) =>
    bffRequest((client) =>
      getLinkedUserAccountsForCustomer({
        client,
        path: { customerId },
        throwOnError: true,
      }),
    )
      .then((envelope) => envelope?.data?.[0])
      .then((account) => {
        if (account) {
          dispatch(setCustomer(account));
        }
      })
      .catch(console.error);

export const fetchCustomer =
  (
    customerId: number,
    isLinked: boolean | null = null,
    customerAccountId?: string,
  ) =>
  (dispatch: Dispatch, getState: () => State) =>
    Promise.all([
      bffRequest((client) =>
        getCustomer({ client, path: { customerId }, throwOnError: true }),
      ),
      bffRequest((client) =>
        getLinkedUserAccountsForCustomer({
          client,
          path: { customerId },
          throwOnError: true,
        }),
      ),
    ])
      .then(([customerEnvelope, accountEnvelope]) => {
        if (!customerEnvelope) {
          console.error('Kunde inte hämta kundinformation');
          return;
        }

        const customer = customerEnvelope?.data?.[0] as Customer;
        const account = accountEnvelope?.data?.[0];
        const accountId =
          customerAccountId ||
          customer?.customerAccounts?.[0]?.customerAccountId;
        !isLinked && dispatch(setCustomer({ ...account, ...customer }));
        dispatch(setSelectedCustomer({ ...account, ...customer, isLinked }));
        dispatch(setSelectedCustomerAccountId(accountId));

        if (customerIsCompany(customer)) {
          dispatch(
            fetchOrganizationStructure(customer.customerId.toString()) as any,
          );
        } else {
          dispatch(setOrganizationStructure(null));
        }
      })
      .then(async () => {
        const selectedCustomer = getSelectedCustomer(getState());
        let initFacility: Property | undefined;
        if (selectedCustomer) {
          initFacility =
            (first(selectedCustomer?.groups) as Property) ||
            first(selectedCustomer?.facilities);
        }

        if (initFacility === undefined) {
          initFacility = {} as Property;
        }

        dispatch(setSelectedProperty(initFacility));

        try {
          const isIndividual = userIsIndividual(selectedCustomer);
          // @ts-expect-error -- window.isOrganization is not typed
          window.isOrganization = !isIndividual;
        } catch (error) {
          console.error(error);
        }

        return selectedCustomer;
      })
      .catch(console.error);

export const setNonCustomer =
  (userIsInactive: boolean) => (dispatch: Dispatch, getState: () => State) => {
    return new Promise((resolve) => {
      Promise.all([
        dispatch(
          setSelectedProperty({
            infraServices: [],
          }),
        ),
        dispatch(
          setSelectedCustomer({
            customerId: NON_CUSTOMER_CUSTOMER_ID,
            status: { id: 0 },
            ...(userIsInactive ? getState().user.selectedCustomer : []),
          }),
        ),
        !userIsInactive && dispatch(setSelectedCustomerAccountId(null)),
      ])
        .then(resolve)
        .catch(console.error);
    });
  };

export const updateUser =
  (values: { email: string; phoneNumber: string }, customerId: number) =>
  (dispatch: Dispatch) => {
    const updateFunc = bffRequest((client) =>
      updateCustomerAccountDetails({
        client,
        path: { customerId },
        body: values,
        throwOnError: true,
      }),
    );
    return updateFunc.then(() => {
      dispatch(setUserDetails(values));
    });
  };

export const deleteLinkedUser =
  (user: LinkedUser, customerIdToDelete?: number) =>
  (dispatch: Dispatch, getState: () => State) => {
    const customerId =
      customerIdToDelete || getSelectedCustomer(getState())?.customerId;

    return bffRequest((client) =>
      removeUserAccountsFromCustomer({
        client,
        path: { customerId: Number(customerId), userId: String(user.userId) },
        throwOnError: true,
      }),
    )
      .then(() => {
        dispatch(removeLinkedUser({ user, customerId }));
      })
      .catch(console.error);
  };

const selectedCustomer = (state: State) => state.user.selectedCustomer;
const selectedCustomerAccountId = (state: State) =>
  state.user.selectedCustomerAccountId;
const account = (state: State) => state.user.account;

export const getSelectedCustomer = createSelector(
  [selectedCustomer, selectedCustomerAccountId, account],
  (customer: Customer, accountId: string, account: Account) => {
    return (
      customer
        ? {
            ...customer,
            ...(find(
              customer.customerAccounts,
              (a) => a.customerAccountId === accountId,
            )
              ? find(
                  customer.customerAccounts,
                  (a) => a.customerAccountId === accountId,
                )
              : account),
            facilities: customerIsMixed(customer)
              ? filter(customer.facilities, (f) =>
                  includes(f.customerAccountIds, accountId),
                )
              : customer.facilities,
            groups: customer.groups,
            useNewLandingPage: customer.useNewLandingPage || false,
          }
        : null
    ) as Customer | null;
  },
);

export const addCompanyAccount =
  (values: {
    companyIdentificationNumber: string;
    companyCustomerNumber: string;
    userName: string;
    userEmail: string;
  }) =>
  () => {
    return bffRequest((client) =>
      addCompanyAccountForUser({
        client,
        body: values,
        throwOnError: true,
      }),
    )
      .then((envelope) => envelope?.data?.[0])
      .catch((error) => {
        return error?.data?.[0] || error?.error;
      });
  };

export const fetchSelectedCustomerFacilities =
  () => async (dispatch: Dispatch, getState: () => State) => {
    const selectedCustomer = getSelectedCustomer(getState());
    if (selectedCustomer?.customerId) {
      const client = await createMittBffClient();
      return getCustomerFacilities({
        client,
        path: {
          customerId: selectedCustomer.customerId,
        },
        query: {
          pageSize: 10000,
        },
      })
        .then((response) => response.data?.data?.[0]?.items ?? [])
        .then((facilities) => {
          const newSelectedProperty = find(
            facilities,
            (f) =>
              f.facilityKey === getState()?.user?.selectedProperty?.facilityKey,
          );
          if (newSelectedProperty) {
            dispatch(
              setSelectedProperty({
                ...getState()?.user?.selectedProperty,
                ...newSelectedProperty,
              }),
            );
          }
          return dispatch(setSelectedCustomerFacilities(facilities));
        })
        .catch(console.error);
    }
  };

export type PanelParticipationResponse = {
  customerNo: number;
  participationStatus: boolean;
};

export const fetchPanelParticipationStatus = async (
  customerNumber: string,
  isNackaCustomer: boolean,
) => {
  const data = await bffRequest((client) =>
    getCustomerPanelParticipation({
      client,
      path: { customerNo: Number(customerNumber) },
      query: isNackaCustomer ? { isNackaCustomer } : undefined,
      throwOnError: true,
    }),
  );
  return data.participationStatus as boolean;
};

export const updatePanelParticipationStatus = async (
  customerNumber: string,
  newParticipationStatus: boolean,
  isNackaCustomer: boolean,
) => {
  return bffRequest((client) =>
    updateCustomerPanelParticipation({
      client,
      path: { customerNo: Number(customerNumber) },
      query: isNackaCustomer ? { isNackaCustomer } : undefined,
      body: newParticipationStatus,
      throwOnError: true,
    }),
  );
};

export default userSlice.reducer;
